import { Badge, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { formatMaintenanceEnd } from "../../lib/date.js";

interface Props {
  status: "green" | "orange" | "red" | "none";
  stale: boolean;
  expired: boolean;
  maintenanceEnd: string | null;
  granularity: "tag" | "quartal" | null;
  inheritedFromLine: boolean;
  /** Ob mindestens einer der beiden Vorlaufwerte vom Produkt kommt (sonst rein global). */
  thresholdsFromProduct: boolean;
  /** Tatsächlich angewandte Vorlaufwerte — nur zur Anzeige, wenn `thresholdsFromProduct`. */
  warnOrangeMonths: number;
  warnRedMonths: number;
  /**
   * Befund U10: Ab wie vielen Monaten ohne Bestätigung ein Erhebungsdatum als
   * veraltet gilt. Der Hinweis „Stand veraltet" nannte seine Schwelle nicht —
   * der Anwender sah die Markierung, konnte sie aber nicht einordnen.
   */
  staleAfterMonths: number;
}

const COLORS = { green: "green", orange: "orange", red: "red", none: "gray" } as const;

/**
 * Ampel eines Softwarestands. Zeigt bewusst mit, WOHER das Datum kommt und ob
 * der Stand veraltet ist — sonst wirkt ein geerbtes Datum oder eine
 * abweichende Produktschwelle wie ein Fehler (Spec §5, §6).
 *
 * `none` heißt „kein Urteil möglich" (nicht proaktiv gewartet oder Ende
 * unbekannt) und wird bewusst grau statt grün gezeigt — Grün auf ungeprüfter
 * Grundlage ist eine Entwarnung, die niemand gegeben hat.
 */
export function LifecycleBadge(props: Props) {
  const { t } = useTranslation();

  // Befund U7: Das Badge zeigte das Wartungsende roh als "2027-06-30",
  // während der Rest des Werkzeugs "30.06.2027" schreibt.
  const label =
    props.maintenanceEnd === null
      ? t("maintenance.lifecycle.unknown")
      : formatMaintenanceEnd(props.maintenanceEnd, props.granularity);

  const hints = [
    props.inheritedFromLine ? t("maintenance.lifecycle.inherited") : null,
    props.stale
      ? t("maintenance.lifecycle.stale", { months: props.staleAfterMonths })
      : null,
    props.expired ? t("maintenance.lifecycle.expired") : null,
    props.thresholdsFromProduct
      ? t("maintenance.lifecycle.productThresholds", {
          orange: props.warnOrangeMonths,
          red: props.warnRedMonths,
        })
      : null,
  ].filter((hint): hint is string => hint !== null);

  return (
    <Tooltip label={hints.join(" · ")} disabled={hints.length === 0}>
      <Badge
        color={COLORS[props.status]}
        variant={props.stale ? "outline" : "filled"}
        style={props.stale ? { textDecoration: "line-through" } : undefined}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}
